import { putFile, unwrap, unwrapBlob, withSignal } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ProcurementClient } from "@nexxt/procurement-ui";
import { api } from "./api";

// The purchase order and inbound screens on gateway-atelier (ADR-169, ADR-171): one call per operation, input and
// answer of its contract. The gateway has the operations of the supplier and of the brand alone
// (ExternalProcurementService, ADR-168, ADR-170), and the upload of a file.

const { procurement } = api;
const byId = procurement["purchase-orders"][":purchaseOrderId"];
const { inbounds } = procurement;
const inbound = inbounds[":inboundId"];

export const procurementClient: ProcurementClient = {
  listPurchaseOrders: (input, signal) =>
    unwrap(procurement["purchase-orders"].$get({ query: filterQuery(input) }, withSignal(signal))),
  getPurchaseOrderOptions: (_, signal) => unwrap(procurement["purchase-order-options"].$get({}, withSignal(signal))),
  getPurchaseOrder: (param, signal) =>
    unwrap(procurement["purchase-orders"][":key"].$get({ param }, withSignal(signal))),
  getPurchaseOrderHistory: ({ purchaseOrderId, ...query }, signal) =>
    unwrap(byId.history.$get({ param: { purchaseOrderId }, query: filterQuery(query) }, withSignal(signal))),
  confirmPurchaseOrder: ({ purchaseOrderId, version }) =>
    unwrap(byId.confirm.$post({ param: { purchaseOrderId }, json: { version } })),
  listPurchaseOrderLines: ({ purchaseOrderId, ...query }, signal) =>
    unwrap(byId.lines.$get({ param: { purchaseOrderId }, query: filterQuery(query) }, withSignal(signal))),
  listPoChanges: ({ purchaseOrderId, ...query }, signal) =>
    unwrap(byId.changes.$get({ param: { purchaseOrderId }, query: filterQuery(query) }, withSignal(signal))),
  proposePoChanges: ({ purchaseOrderId, ...json }) => unwrap(byId.changes.$post({ param: { purchaseOrderId }, json })),
  withdrawPoChanges: ({ purchaseOrderId, ...json }) =>
    unwrap(byId.changes.withdraw.$post({ param: { purchaseOrderId }, json })),
  listPurchaseOrderRevisions: (param, signal) => unwrap(byId.revisions.$get({ param }, withSignal(signal))),
  getPurchaseOrderRevision: ({ purchaseOrderId, revisionNo }, signal) =>
    unwrap(
      byId.revisions[":revisionNo"].$get(
        { param: { purchaseOrderId, revisionNo: String(revisionNo) } },
        withSignal(signal),
      ),
    ),
  listInbounds: (input, signal) => unwrap(inbounds.$get({ query: filterQuery(input) }, withSignal(signal))),
  getInbound: (param, signal) => unwrap(inbounds[":key"].$get({ param }, withSignal(signal))),
  getInboundHistory: ({ inboundId, ...query }, signal) =>
    unwrap(inbound.history.$get({ param: { inboundId }, query: filterQuery(query) }, withSignal(signal))),
  listInboundCandidates: (param, signal) => unwrap(byId["inbound-candidates"].$get({ param }, withSignal(signal))),
  createInbound: (json) => unwrap(inbounds.$post({ json })),
  updateInbound: ({ inboundId, ...json }) => unwrap(inbound.$patch({ param: { inboundId }, json })),
  trackInbound: ({ inboundId, ...json }) => unwrap(inbound.tracking.$put({ param: { inboundId }, json })),
  updateInboundPickup: ({ inboundId, ...json }) => unwrap(inbound.pickup.$put({ param: { inboundId }, json })),
  submitInbound: ({ inboundId, version }) => unwrap(inbound.submit.$post({ param: { inboundId }, json: { version } })),
  authorizeInbound: ({ inboundId, ...json }) => unwrap(inbound.authorize.$post({ param: { inboundId }, json })),
  refuseInbound: ({ inboundId, ...json }) => unwrap(inbound.refuse.$post({ param: { inboundId }, json })),
  cancelInbound: ({ inboundId, ...json }) => unwrap(inbound.cancel.$post({ param: { inboundId }, json })),
  attachInboundDocuments: ({ inboundId, ...json }) => unwrap(inbound.documents.$post({ param: { inboundId }, json })),
  removeInboundDocument: ({ inboundId, documentId, version }) =>
    unwrap(
      inbound.documents[":documentId"].$delete({
        param: { inboundId, documentId },
        query: { version: String(version) },
      }),
    ),
  downloadInboundDocument: (param, signal) =>
    unwrapBlob(inbound.documents[":documentId"].content.$get({ param }, withSignal(signal))),
  inboundDocumentUrl: (param) => inbound.documents[":documentId"].content.$url({ param }).href,
  listInboundNotes: (param, signal) => unwrap(inbound.notes.$get({ param }, withSignal(signal))),
  addInboundNote: ({ inboundId, ...json }) => unwrap(inbound.notes.$post({ param: { inboundId }, json })),
  editInboundNote: ({ inboundId, noteId, ...json }) =>
    unwrap(inbound.notes[":noteId"].$patch({ param: { inboundId, noteId }, json })),
  deleteInboundNote: ({ inboundId, noteId, version }) =>
    unwrap(inbound.notes[":noteId"].$delete({ param: { inboundId, noteId }, query: { version: String(version) } })),
  settleInboundShortage: ({ inboundId, ...json }) => unwrap(inbound.settle.$post({ param: { inboundId }, json })),
  listCreditNotes: (input, signal) =>
    unwrap(procurement["credit-notes"].$get({ query: filterQuery(input) }, withSignal(signal))),
  sendCreditNote: ({ creditNoteId, ...json }) =>
    unwrap(procurement["credit-notes"][":creditNoteId"].file.$post({ param: { creditNoteId }, json })),
  uploadInboundFile: (file) => putFile(api.files.files[":fileId"], file, "inbound"),
};
